import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router";
import { apiFetch } from "@/lib/api-client";
import { formatNumber } from "@/lib/i18n";
import { SkeletonCard } from "@/components/skeleton";

/**
 * G.20: Simulcast to YouTube/Facebook/Twitch/LinkedIn/Custom RTMP
 * - Configure RTMP targets with encrypted stream keys
 * - Start/stop multi-output simulcast via LiveKit Egress
 * - Real-time per-target status dashboard
 */

type Platform = "youtube" | "facebook" | "twitch" | "linkedin" | "custom";
type TargetStatus = "idle" | "connecting" | "live" | "error" | "stopped";

interface SimulcastTarget {
  id: string;
  platform: Platform;
  label: string;
  rtmpUrl: string;
  status: TargetStatus;
  startedAt: string | null;
  viewerCount: number | null;
  errorMessage: string | null;
  uptimeSeconds: number | null;
}

interface TargetsResponse {
  targets: SimulcastTarget[];
}

interface StatusResponse {
  isSimulcasting: boolean;
  targets: SimulcastTarget[];
}

const PLATFORM_LABELS: Record<Platform, string> = {
  youtube: "YT",
  facebook: "FB",
  twitch: "TW",
  linkedin: "LI",
  custom: "Custom",
};

const PLATFORM_NAMES: Record<Platform, string> = {
  youtube: "YouTube",
  facebook: "Facebook",
  twitch: "Twitch",
  linkedin: "LinkedIn",
  custom: "Custom RTMP",
};

const PLATFORM_PRESETS: Partial<Record<Platform, string>> = {
  youtube: "rtmp://a.rtmp.youtube.com/live2",
  facebook: "rtmps://live-api-s.facebook.com:443/rtmp/",
  twitch: "rtmp://live.twitch.tv/app",
  linkedin: "rtmps://1.rtmp.linkedin.com:443/rtmpdata/",
};

const STATUS_STYLES: Record<TargetStatus, { dot: string; bg: string; label: string }> = {
  idle: { dot: "bg-[var(--color-muted-foreground)]", bg: "bg-muted", label: "Idle" },
  connecting: { dot: "bg-[var(--color-warning)]/50", bg: "bg-[var(--color-warning)]/5", label: "Connecting..." },
  live: { dot: "bg-[var(--color-success)]/50", bg: "bg-[var(--color-success)]/10", label: "LIVE" },
  error: { dot: "bg-[var(--color-destructive)]/50", bg: "bg-[var(--color-destructive)]/5", label: "Error" },
  stopped: { dot: "bg-[var(--color-muted-foreground)]", bg: "bg-muted", label: "Stopped" },
};

function formatUptime(seconds: number | null): string {
  if (seconds === null || seconds <= 0) return "--:--:--";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
}

export default function SimulcastPage() {
  const { webinarId } = useParams();
  const webinarBasePath = webinarId ? `/v1/webinars/${webinarId}` : null;
  const [targets, setTargets] = useState<SimulcastTarget[]>([]);
  const [isSimulcasting, setIsSimulcasting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add target form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [formPlatform, setFormPlatform] = useState<Platform>("youtube");
  const [formLabel, setFormLabel] = useState("");
  const [formRtmpUrl, setFormRtmpUrl] = useState(PLATFORM_PRESETS.youtube ?? "");
  const [formStreamKey, setFormStreamKey] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [showStreamKey, setShowStreamKey] = useState(false);

  // Fetch targets on mount
  const fetchTargets = useCallback(async () => {
    if (!webinarBasePath) return;
    try {
      const res = await apiFetch<TargetsResponse>(
        `${webinarBasePath}/simulcast/targets`,
      );
      setTargets(res.targets);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load targets");
    } finally {
      setIsLoading(false);
    }
  }, [webinarBasePath]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  // Poll status when simulcasting
  useEffect(() => {
    if (!webinarBasePath) return;
    const hasActive = targets.some(
      (t) => t.status === "live" || t.status === "connecting",
    );
    if (!hasActive) {
      setIsSimulcasting(false);
      return;
    }

    setIsSimulcasting(true);

    const interval = setInterval(async () => {
      try {
        const res = await apiFetch<StatusResponse>(
          `${webinarBasePath}/simulcast/status`,
        );
        setTargets(res.targets);
        setIsSimulcasting(res.isSimulcasting);
      } catch {
        // Retry on next interval
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [webinarBasePath, targets]);

  // Handle platform change — auto-fill RTMP URL
  const handlePlatformChange = (platform: Platform) => {
    setFormPlatform(platform);
    const preset = PLATFORM_PRESETS[platform];
    if (preset) {
      setFormRtmpUrl(preset);
    } else {
      setFormRtmpUrl("");
    }
  };

  const handleAddTarget = useCallback(async () => {
    if (!webinarBasePath || isAdding) return;
    if (!formLabel.trim() || !formRtmpUrl.trim() || !formStreamKey.trim()) {
      setError("All fields are required");
      return;
    }

    setIsAdding(true);
    setError(null);

    try {
      await apiFetch(`${webinarBasePath}/simulcast/targets`, {
        method: "POST",
        body: JSON.stringify({
          platform: formPlatform,
          label: formLabel.trim(),
          rtmpUrl: formRtmpUrl.trim(),
          streamKey: formStreamKey.trim(),
        }),
      });

      // Reset form
      setFormLabel("");
      setFormStreamKey("");
      setShowStreamKey(false);
      setShowAddForm(false);
      await fetchTargets();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add target");
    } finally {
      setIsAdding(false);
    }
  }, [fetchTargets, formLabel, formPlatform, formRtmpUrl, formStreamKey, isAdding, webinarBasePath]);

  const handleRemoveTarget = async (targetId: string) => {
    if (!webinarBasePath) return;
    try {
      await apiFetch(`${webinarBasePath}/simulcast/targets/${targetId}`, {
        method: "DELETE",
      });
      await fetchTargets();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove target");
    }
  };

  const handleStartSimulcast = async () => {
    if (!webinarBasePath || isStarting) return;
    setIsStarting(true);
    setError(null);

    try {
      await apiFetch(`${webinarBasePath}/simulcast/start`, {
        method: "POST",
      });
      // Immediately poll status
      const res = await apiFetch<StatusResponse>(
        `${webinarBasePath}/simulcast/status`,
      );
      setTargets(res.targets);
      setIsSimulcasting(res.isSimulcasting);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start simulcast");
    } finally {
      setIsStarting(false);
    }
  };

  const handleStopSimulcast = async () => {
    if (!webinarBasePath || isStopping) return;
    setIsStopping(true);
    setError(null);

    try {
      await apiFetch(`${webinarBasePath}/simulcast/stop`, {
        method: "POST",
      });
      await fetchTargets();
      setIsSimulcasting(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to stop simulcast");
    } finally {
      setIsStopping(false);
    }
  };

  if (isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <SkeletonCard key={index} className="h-40" />
        ))}
      </div>
    );
  }

  const idleTargets = targets.filter(
    (t) => t.status === "idle" || t.status === "stopped" || t.status === "error",
  );
  const canStart = idleTargets.length > 0 && !isSimulcasting;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Simulcast</h2>
          <p className="text-sm text-muted-foreground">
            Stream to YouTube, Facebook, Twitch, LinkedIn, or any RTMP destination
          </p>
        </div>
        <div className="flex gap-2">
          {canStart && (
            <button
              onClick={handleStartSimulcast}
              disabled={isStarting}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isStarting ? "Starting..." : "Start Simulcast"}
            </button>
          )}
          {isSimulcasting && (
            <button
              onClick={handleStopSimulcast}
              disabled={isStopping}
              className="rounded-md bg-[var(--color-destructive)]/50 px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-destructive)] disabled:opacity-50"
            >
              {isStopping ? "Stopping..." : "Stop All"}
            </button>
          )}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-md bg-[var(--color-destructive)]/5 p-3 text-sm text-[var(--color-destructive)]">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 font-medium underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Live status banner */}
      {isSimulcasting && (
        <div className="flex items-center gap-2 rounded-md bg-[var(--color-success)]/10 p-3">
          <span className="relative flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--color-success)]/50 opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-[var(--color-success)]/50" />
          </span>
          <span className="text-sm font-medium text-[var(--color-success)]">
            Simulcasting to {targets.filter((t) => t.status === "live").length} target(s)
          </span>
        </div>
      )}

      {/* Target cards */}
      {targets.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {targets.map((target) => {
            const style = STATUS_STYLES[target.status];
            return (
              <div
                key={target.id}
                className="stagger-item rounded-lg border border-border bg-background p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-accent px-2 py-0.5 text-xs font-bold text-foreground">
                      {PLATFORM_LABELS[target.platform]}
                    </span>
                    <span className="text-sm font-medium text-foreground">
                      {target.label}
                    </span>
                  </div>
                  {target.status !== "live" && target.status !== "connecting" && (
                    <button
                      onClick={() => handleRemoveTarget(target.id)}
                      className="text-xs text-muted-foreground hover:text-[var(--color-destructive)]"
                      title="Remove target"
                    >
                      Remove
                    </button>
                  )}
                </div>

                {/* Status badge */}
                <div className="mt-3 flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${style.dot}`} />
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-medium ${style.bg} ${
                      target.status === "live"
                        ? "text-[var(--color-success)]"
                        : target.status === "error"
                          ? "text-[var(--color-destructive)]"
                          : "text-foreground"
                    }`}
                  >
                    {style.label}
                  </span>
                </div>

                {/* Uptime */}
                {(target.status === "live" || target.status === "connecting") && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Uptime: {formatUptime(target.uptimeSeconds)}
                  </div>
                )}

                {/* Viewer count */}
                {target.viewerCount !== null && target.status === "live" && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Viewers: {formatNumber(target.viewerCount)}
                  </div>
                )}

                {/* Error message */}
                {target.status === "error" && target.errorMessage && (
                  <div className="mt-2 rounded bg-[var(--color-destructive)]/5 p-2 text-xs text-[var(--color-destructive)]">
                    {target.errorMessage}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {targets.length === 0 && !showAddForm && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No simulcast targets configured. Add a streaming destination to get started.
          </p>
        </div>
      )}

      {/* Add target button */}
      {!showAddForm && targets.length < 5 && (
        <button
          onClick={() => setShowAddForm(true)}
          className="rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-accent"
        >
          + Add Streaming Target
        </button>
      )}

      {/* Add target form */}
      {showAddForm && (
        <div className="rounded-lg border border-border bg-background p-5">
          <h3 className="mb-4 text-sm font-medium text-foreground">
            Add Streaming Target
          </h3>

          {/* Platform selector */}
          <div className="mb-4">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Platform
            </label>
            <div className="flex gap-2">
              {(
                ["youtube", "facebook", "twitch", "linkedin", "custom"] as const
              ).map((p) => (
                <button
                  key={p}
                  onClick={() => handlePlatformChange(p)}
                  className={`rounded-md border px-3 py-2 text-sm ${
                    formPlatform === p
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border text-foreground hover:bg-accent"
                  }`}
                >
                  {PLATFORM_LABELS[p]}{" "}
                  <span className="hidden sm:inline">
                    {PLATFORM_NAMES[p]}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Label */}
          <div className="mb-4">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Label
            </label>
            <input
              type="text"
              value={formLabel}
              onChange={(e) => setFormLabel(e.target.value)}
              placeholder={`e.g. ${PLATFORM_NAMES[formPlatform]} Main Channel`}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
            />
          </div>

          {/* RTMP URL */}
          <div className="mb-4">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              RTMP URL
            </label>
            <input
              type="text"
              value={formRtmpUrl}
              onChange={(e) => setFormRtmpUrl(e.target.value)}
              placeholder="rtmp://..."
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono text-foreground placeholder:text-muted-foreground"
            />
            {PLATFORM_PRESETS[formPlatform] && (
              <p className="mt-1 text-xs text-muted-foreground">
                Pre-filled with {PLATFORM_NAMES[formPlatform]} default server
              </p>
            )}
          </div>

          {/* Stream Key */}
          <div className="mb-4">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Stream Key
            </label>
            <div className="relative">
              <input
                type={showStreamKey ? "text" : "password"}
                value={formStreamKey}
                onChange={(e) => setFormStreamKey(e.target.value)}
                placeholder="Paste your stream key"
                className="w-full rounded-md border border-border bg-background px-3 py-2 pr-16 text-sm font-mono text-foreground placeholder:text-muted-foreground"
              />
              <button
                type="button"
                onClick={() => setShowStreamKey(!showStreamKey)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
              >
                {showStreamKey ? "Hide" : "Show"}
              </button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Encrypted at rest. Never stored in plaintext.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleAddTarget}
              disabled={isAdding}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isAdding ? "Adding..." : "Add Target"}
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setFormLabel("");
                setFormStreamKey("");
                setShowStreamKey(false);
                setError(null);
              }}
              className="rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-accent"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
